import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wrench,
  RefreshCw,
  Fan,
  Wind,
  ArrowRight,
} from "lucide-react";

const ALL = [
  {
    slug: "ac-repair",
    to: "/services/ac-repair",
    icon: Wrench,
    title: "AC Repair",
    desc: "AC not cooling, leaking, or making strange sounds? Start a clear service request.",
  },
  {
    slug: "maintenance",
    to: "/services/maintenance",
    icon: RefreshCw,
    title: "Maintenance & Tune-Ups",
    desc: "Pre-summer tune-ups, drain line flushes, and a Florida-friendly maintenance plan concept.",
  },
  {
    slug: "replacement",
    to: "/services/replacement",
    icon: Fan,
    title: "System Replacement",
    desc: "Right-sized swap-outs, efficiency basics, and a quote-ready intake when repair stops adding up.",
  },
  {
    slug: "indoor-air-quality",
    to: "/services/indoor-air-quality",
    icon: Wind,
    title: "Indoor Air Quality",
    desc: "Humidity control, filtration, UV, and ventilation options for coastal Florida homes.",
  },
];

export default function RelatedServices({ currentSlug, eyebrow = "Related services" }) {
  const items = ALL.filter((s) => s.slug !== currentSlug);
  return (
    <section
      data-testid="related-services"
      aria-labelledby="related-services-heading"
      className="relative bg-white py-20 sm:py-24"
    >
      <div className="container-page">
        <div className="max-w-2xl">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
            {eyebrow}
          </span>
          <h2
            id="related-services-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            Keep exploring.
          </h2>
        </div>

        <motion.ul
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.06 } },
          }}
          className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {items.map((s) => {
            const Icon = s.icon;
            return (
              <motion.li
                key={s.slug}
                variants={{
                  hidden: { opacity: 0, y: 14 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
                }}
              >
                <Link
                  to={s.to}
                  data-testid={`related-services-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-mist-line bg-white p-6 shadow-soft transition-all hover:-translate-y-1 hover:border-aqua/40 hover:shadow-cyanGlow"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-aqua shadow-soft">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-navy">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {s.desc}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-aqua-deep transition-transform group-hover:translate-x-0.5">
                    Learn more
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
}
